import { useMemo, useState } from "react";
import Select from "react-select";
import { POSITIONS_COLOR } from "../../utils/constants";
import {
  TradeModalOverlay,
  TradeModalCard,
  TradeModalHeader,
  TradeModalClose,
  TradeTeamSelector,
  TradeColumnsWrapper,
  TradeColumn,
  TradeColumnHeader,
  TradePlayerList,
  TradePlayerRow,
  TradeEmptyState,
  TradeModalFooter,
  TradeCancelButton,
  TradeSubmitButton,
  ConfirmBody,
  ConfirmSection,
  ConfirmSectionTitle,
  ConfirmPlayerCard,
  ConfirmDivider,
  ConfirmArrow,
} from "./ProposeTradeModal.styles";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderColor: "#E9EBEF",
    boxShadow: "none",
    fontSize: 13,
    "&:hover": { borderColor: "#c5cad1" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  option: (base) => ({ ...base, fontSize: 13 }),
  singleValue: (base) => ({ ...base, fontSize: 13 }),
};

const STEP_SELECT = "select";
const STEP_CONFIRM = "confirm";

function ProposeTradeModal({
  isOpen,
  onClose,
  onPropose,
  teamList,
  slots,
  drafted,
  myTeamId,
  myTeamName,
  myTeamLogo,
}) {
  const [step, setStep] = useState(STEP_SELECT);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTheirPlayerIds, setSelectedTheirPlayerIds] = useState(new Set());
  const [selectedMyPlayerIds, setSelectedMyPlayerIds] = useState(new Set());
  const [note, setNote] = useState("");

  const userControlledTeamIds = useMemo(() => {
    const ids = new Set();
    (slots || []).forEach((slot) => {
      if (slot.user_id != null && slot.team_id != null) {
        ids.add(slot.team_id);
      }
    });
    return ids;
  }, [slots]);

  const teamOptions = useMemo(
    () =>
      teamList
        .filter((team) => team.id !== myTeamId && userControlledTeamIds.has(team.id))
        .map((team) => ({
          value: team.id,
          label: team.name,
          logo: team.logo,
        })),
    [teamList, myTeamId, userControlledTeamIds]
  );

  const selectedTeamOption = useMemo(
    () => teamOptions.find((opt) => opt.value === selectedTeamId) || null,
    [teamOptions, selectedTeamId]
  );

  const selectedTeam = useMemo(
    () => teamList.find((t) => t.id === selectedTeamId) || null,
    [teamList, selectedTeamId]
  );

  const theirRoster = useMemo(() => {
    if (!selectedTeamId) return [];
    return drafted
      .filter((entry) => entry.teamId === selectedTeamId)
      .map((entry) => ({
        id: entry.playerId,
        pickId: entry.pickId,
        pos: entry.position || "-",
        player: entry.playerName || "-",
        rank: entry.ranking ?? "-",
      }));
  }, [drafted, selectedTeamId]);

  const myRoster = useMemo(() => {
    if (!myTeamId) return [];
    return drafted
      .filter((entry) => entry.teamId === myTeamId)
      .map((entry) => ({
        id: entry.playerId,
        pickId: entry.pickId,
        pos: entry.position || "-",
        player: entry.playerName || "-",
        rank: entry.ranking ?? "-",
      }));
  }, [drafted, myTeamId]);

  const selectedTheirPlayers = useMemo(
    () => theirRoster.filter((p) => selectedTheirPlayerIds.has(p.id)),
    [theirRoster, selectedTheirPlayerIds]
  );

  const selectedMyPlayers = useMemo(
    () => myRoster.filter((p) => selectedMyPlayerIds.has(p.id)),
    [myRoster, selectedMyPlayerIds]
  );

  const toggleTheirPlayer = (playerId) => {
    setSelectedTheirPlayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  const toggleMyPlayer = (playerId) => {
    setSelectedMyPlayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  const handleTeamChange = (option) => {
    setSelectedTeamId(option ? option.value : null);
    setSelectedTheirPlayerIds(new Set());
  };

  const resetState = () => {
    setStep(STEP_SELECT);
    setSelectedTeamId(null);
    setSelectedTheirPlayerIds(new Set());
    setSelectedMyPlayerIds(new Set());
    setNote("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleContinue = () => {
    setStep(STEP_CONFIRM);
  };

  const handleBack = () => {
    setStep(STEP_SELECT);
  };

  const handleSubmit = () => {
    if (onPropose) {
      onPropose({
        counterpartyTeamId: selectedTeamId,
        proposerPlayerIds: Array.from(selectedMyPlayerIds),
        counterpartyPlayerIds: Array.from(selectedTheirPlayerIds),
        note: note.trim(),
      });
    }
    handleClose();
  };

  const canContinue =
    selectedTeamId &&
    selectedTheirPlayerIds.size > 0 &&
    selectedMyPlayerIds.size > 0;

  if (!isOpen) return null;

  return (
    <TradeModalOverlay onClick={handleClose}>
      <TradeModalCard onClick={(e) => e.stopPropagation()}>
        <TradeModalHeader>
          <h3>{step === STEP_SELECT ? "Propose Trade" : "Confirm Transaction"}</h3>
          <TradeModalClose type="button" onClick={handleClose} aria-label="Close trade modal">
            ×
          </TradeModalClose>
        </TradeModalHeader>

        {step === STEP_SELECT && (
          <>
            <TradeTeamSelector>
              <span>Trade with:</span>
              <div className="select-wrapper">
                <Select
                  value={selectedTeamOption}
                  options={teamOptions}
                  onChange={handleTeamChange}
                  isSearchable={false}
                  styles={selectStyles}
                  placeholder="Select a team..."
                  formatOptionLabel={(option) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {option.logo && (
                        <img
                          src={option.logo}
                          alt={option.label}
                          style={{ width: 18, height: 18, objectFit: "contain" }}
                        />
                      )}
                      <span>{option.label}</span>
                    </div>
                  )}
                />
              </div>
            </TradeTeamSelector>

            <TradeColumnsWrapper>
              <TradeColumn>
                <TradeColumnHeader>
                  {selectedTeam?.logo && (
                    <img src={selectedTeam.logo} alt={selectedTeam.name} />
                  )}
                  {selectedTeam ? selectedTeam.name : "Select a team"}
                </TradeColumnHeader>
                <TradePlayerList>
                  {!selectedTeamId && (
                    <TradeEmptyState>Select a team to see their players</TradeEmptyState>
                  )}
                  {selectedTeamId && theirRoster.length === 0 && (
                    <TradeEmptyState>No players on this roster</TradeEmptyState>
                  )}
                  {theirRoster.map((p) => {
                    const isSelected = selectedTheirPlayerIds.has(p.id);
                    return (
                      <TradePlayerRow
                        key={`their-${p.id}-${p.pickId}`}
                        className={isSelected ? "selected" : ""}
                        onClick={() => toggleTheirPlayer(p.id)}
                      >
                        <span className="check-icon">{isSelected ? "✓" : ""}</span>
                        <span
                          className="pos-pill"
                          style={{ backgroundColor: POSITIONS_COLOR[p.pos] || "transparent" }}
                        >
                          {p.pos}
                        </span>
                        <span className="player-name">{p.player}</span>
                        <span className="player-rank">{p.rank}</span>
                      </TradePlayerRow>
                    );
                  })}
                </TradePlayerList>
              </TradeColumn>

              <TradeColumn>
                <TradeColumnHeader>
                  {myTeamLogo && <img src={myTeamLogo} alt={myTeamName} />}
                  {myTeamName || "My Team"}
                </TradeColumnHeader>
                <TradePlayerList>
                  {myRoster.length === 0 && (
                    <TradeEmptyState>No players on your roster</TradeEmptyState>
                  )}
                  {myRoster.map((p) => {
                    const isSelected = selectedMyPlayerIds.has(p.id);
                    return (
                      <TradePlayerRow
                        key={`my-${p.id}-${p.pickId}`}
                        className={isSelected ? "selected" : ""}
                        onClick={() => toggleMyPlayer(p.id)}
                      >
                        <span className="check-icon">{isSelected ? "✓" : ""}</span>
                        <span
                          className="pos-pill"
                          style={{ backgroundColor: POSITIONS_COLOR[p.pos] || "transparent" }}
                        >
                          {p.pos}
                        </span>
                        <span className="player-name">{p.player}</span>
                        <span className="player-rank">{p.rank}</span>
                      </TradePlayerRow>
                    );
                  })}
                </TradePlayerList>
              </TradeColumn>
            </TradeColumnsWrapper>

            <TradeModalFooter>
              <TradeCancelButton type="button" onClick={handleClose}>
                Cancel
              </TradeCancelButton>
              <TradeSubmitButton type="button" disabled={!canContinue} onClick={handleContinue}>
                Continue
              </TradeSubmitButton>
            </TradeModalFooter>
          </>
        )}

        {step === STEP_CONFIRM && (
          <>
            <ConfirmBody>
              <ConfirmSection>
                <ConfirmSectionTitle>
                  {selectedTeam?.logo && <img src={selectedTeam.logo} alt={selectedTeam.name} />}
                  Receiving from {selectedTeam?.name}
                </ConfirmSectionTitle>
                {selectedTheirPlayers.map((p) => (
                  <ConfirmPlayerCard key={`confirm-their-${p.id}-${p.pickId}`}>
                    <span
                      className="pos-pill"
                      style={{ backgroundColor: POSITIONS_COLOR[p.pos] || "transparent" }}
                    >
                      {p.pos}
                    </span>
                    <span className="player-name">{p.player}</span>
                    <span className="player-rank">#{p.rank}</span>
                  </ConfirmPlayerCard>
                ))}
              </ConfirmSection>

              <ConfirmArrow>
                ⇄
              </ConfirmArrow>

              <ConfirmSection>
                <ConfirmSectionTitle>
                  {myTeamLogo && <img src={myTeamLogo} alt={myTeamName} />}
                  Offering from {myTeamName || "My Team"}
                </ConfirmSectionTitle>
                {selectedMyPlayers.map((p) => (
                  <ConfirmPlayerCard key={`confirm-my-${p.id}-${p.pickId}`}>
                    <span
                      className="pos-pill"
                      style={{ backgroundColor: POSITIONS_COLOR[p.pos] || "transparent" }}
                    >
                      {p.pos}
                    </span>
                    <span className="player-name">{p.player}</span>
                    <span className="player-rank">#{p.rank}</span>
                  </ConfirmPlayerCard>
                ))}
              </ConfirmSection>
            </ConfirmBody>

            <div style={{ padding: "0 20px 12px" }}>
              <label
                htmlFor="trade-note"
                style={{ fontSize: 12, fontWeight: 600, color: "#5b6675", display: "block", marginBottom: 4 }}
              >
                Note (optional)
              </label>
              <textarea
                id="trade-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a message to your trade proposal..."
                maxLength={200}
                rows={2}
                style={{
                  width: "100%",
                  border: "1px solid #e8edf2",
                  borderRadius: 6,
                  padding: "8px 10px",
                  fontSize: 12,
                  resize: "none",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <TradeModalFooter>
              <TradeCancelButton type="button" onClick={handleBack}>
                Back
              </TradeCancelButton>
              <TradeSubmitButton type="button" onClick={handleSubmit}>
                Send Trade Proposal
              </TradeSubmitButton>
            </TradeModalFooter>
          </>
        )}
      </TradeModalCard>
    </TradeModalOverlay>
  );
}

export default ProposeTradeModal;
